"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import type { ReactNode } from "react";

type StepProgressStateT = {
  onContinue?: () => void;
  onBack?: () => void;
  disabled?: boolean;
  loading?: boolean;
};

type StepProgressContextValueT = {
  state: StepProgressStateT;
  setState: (state: StepProgressStateT) => void;
};

const StepProgressContext = createContext<StepProgressContextValueT | null>(null);

export function StepProgressProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<StepProgressStateT>({});
  const value = useMemo(() => ({ state, setState }), [state]);
  return <StepProgressContext.Provider value={value}>{children}</StepProgressContext.Provider>;
}

export function useStepProgressState(): StepProgressStateT {
  return useContext(StepProgressContext)?.state ?? {};
}

export function useRegisterStepProgress({ onContinue, onBack, disabled, loading }: StepProgressStateT) {
  const ctx = useContext(StepProgressContext);
  const onContinueRef = useRef(onContinue);
  onContinueRef.current = onContinue;
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  const stableOnContinue = useCallback(() => {
    onContinueRef.current?.();
  }, []);

  const stableOnBack = useCallback(() => {
    onBackRef.current?.();
  }, []);

  const setState = ctx?.setState;
  const hasOnBack = !!onBack;

  useEffect(() => {
    if (!setState) {
      return;
    }
    setState({
      onContinue: stableOnContinue,
      onBack: hasOnBack ? stableOnBack : undefined,
      disabled,
      loading,
    });
    return () => setState({});
  }, [disabled, loading, setState, stableOnContinue, stableOnBack, hasOnBack]);
}
